import { formatPrice } from '@/features/operations/money';
import type { NoteEntry } from '@/features/operations/types';

/// Why no automatic amount could be produced, in the language the team uses.
///
/// This matters more at the counter than it does on a listing: there is a person waiting for
/// an answer, and "no pudimos estimar" without a reason leaves whoever is attending with
/// nothing to say. Every reason `resolveQuotationEstimate` can return is named here.
export const estimateUnavailableLabels: Record<string, string> = {
  // Kept only so quotations created with the previous engine remain readable.
  NO_SETTINGS: 'la configuración anterior de cotización estaba incompleta',
  NO_PHONE_MODEL: 'el modelo no está en el catálogo',
  MODEL_NOT_QUOTABLE: 'ese modelo está marcado como no cotizable',
  NO_BASE_PRICE: 'ese modelo y capacidad no tienen precio base cargado',
  INCOMPLETE_ANSWERS: 'falta responder alguno de los campos declarados',
  MISSING_DISCOUNT: 'ese modelo no tiene cargado el monto de alguna de las respuestas',
  BELOW_FLOOR: 'las reglas anteriores no ofrecieron un valor para el equipo',
  NON_POSITIVE_RESULT: 'los descuentos igualan o superan el precio base',
  PRICING_ERROR: 'hubo un problema al calcular',
};

export const originLabels: Record<string, string> = {
  PUBLIC_WEB: 'Web',
  INTERNAL_PANEL: 'Mostrador',
};

/// The state of a customer account (ADR 0008, #229). Pending is a counter account its owner has
/// not activated; active ones differ only in whether the mail was proven.
export const customerStatusLabels: Record<string, string> = {
  PENDING: 'Pendiente',
  UNVERIFIED: 'Activa sin verificar',
  VERIFIED: 'Verificada',
};

/// What each state means for whoever is attending, said next to the chip of the account sheet.
export const customerStatusDescriptions: Record<string, string> = {
  PENDING:
    'La creó PhoneTec y todavía no la activó su dueño: no puede entrar hasta seguir la invitación que le llegó al mail.',
  UNVERIFIED:
    'Ya eligió su contraseña y puede cotizar, pero no confirmó el mail: hasta hacerlo no puede reservar ni comprar.',
  VERIFIED: 'Activa y con el mail confirmado.',
};

/// What a customer did with PhoneTec, as the list names it (#229).
export const customerActivityLabels = {
  quoted: 'Cotizó',
  sold: 'Vendió',
  bought: 'Compró',
} as const;

/// How the panel names the state of the applicant's confirmation email (#163).
///
/// "En cola" says where the message is -- handed to the queue, not yet sent -- rather than a
/// vaguer "pendiente", which reads as a step somebody still has to take.
export const emailDeliveryLabels: Record<string, string> = {
  QUEUED: 'en cola',
  SENT: 'enviado',
  FAILED: 'falló',
  NOT_CONFIGURED: 'sin destinatario configurado',
};

/// How the panel names each device state (ADR 0005, ADR 0007, BR-034). Which states exist and
/// how they follow each other is the backend's to decide; this only reads them.
export const deviceStateLabels: Record<string, string> = {
  PENDING_PUBLICATION: 'Pendiente de publicación',
  PUBLISHED: 'Publicado',
  RESERVED: 'Reservado',
  SOLD: 'Vendido',
};

/// The state of a sale that is no longer in force (BR-034). A sale in force has no label.
export const saleStatusLabels: Record<string, string> = {
  REVERTED: 'Revertida',
};

/// How a sale was paid (P-E4). Mercado Pago is only the online checkout's (VEN-6).
export const paymentMethodLabels: Record<string, string> = {
  CASH: 'Efectivo',
  CARD: 'Tarjeta',
  TRANSFER: 'Transferencia',
  MERCADO_PAGO: 'Mercado Pago',
};

/// What the panel offers when concreting a reservation at the counter (VEN-4).
export const panelPaymentMethodOptions = ['CASH', 'CARD', 'TRANSFER'] as const;

/// Every state, in the order the flow goes through them. Used by the list's filter.
export const deviceStateOptions = Object.keys(deviceStateLabels);

export function getDeviceStateLabel(state: string) {
  return deviceStateLabels[state] || state;
}

export const deviceChangeFieldLabels: Record<string, string> = {
  PAID_PRICE: 'Precio pagado',
  SALE_PRICE: 'Precio de venta',
  COMMERCIAL_MODALITY: 'Modalidad comercial',
  SOLD_AT: 'Fecha de venta',
  WARRANTY_DURATION_MONTHS: 'Duración de la garantía',
  WARRANTY_CONDITIONS: 'Condiciones de la garantía',
};

export const commercialModalityLabels: Record<string, string> = {
  DIRECT_PURCHASE: 'Compra directa',
  TRADE_IN: 'Parte de pago',
  CONSIGNMENT: 'Consignación',
};

const priceFields = new Set(['PAID_PRICE', 'SALE_PRICE']);

export function formatDeviceChangeValue(field: string, value: string | null): string {
  if (priceFields.has(field)) {
    return formatPrice(value === null ? null : Number(value));
  }

  if (field === 'COMMERCIAL_MODALITY' && value) {
    return commercialModalityLabels[value] || value;
  }

  if (field === 'SOLD_AT' && value) {
    return formatHistoryTimestamp(value);
  }

  if (field === 'WARRANTY_DURATION_MONTHS' && value) {
    const months = Number(value);
    return `${months} ${months === 1 ? 'mes' : 'meses'}`;
  }

  return value ?? 'Sin cargar';
}

export function formatQuotationDate(value: string) {
  return new Date(value).toLocaleDateString('es-UY', { dateStyle: 'medium' });
}

/// How the panel names an online payment's state (VEN-6, BR-043).
export const paymentStatusLabels: Record<string, string> = {
  PENDING: 'Pendiente',
  APPROVED: 'Aprobado',
  REJECTED: 'Rechazado',
  CANCELLED: 'Cancelado',
  ERROR: 'Error de comunicación',
  REFUNDED: 'Devuelto',
};

/// What each payment state meant for the buyer and the device, said the way the counter would
/// answer a buyer. Approved without a sale and refunded have their own, longer explanations.
export const paymentStatusDetails: Record<string, string> = {
  PENDING: 'Mercado Pago todavía lo está revisando.',
  APPROVED: 'Cobrado. El equipo se vendió.',
  REJECTED: 'No se cobró. El equipo volvió a publicarse.',
  CANCELLED: 'No se cobró. El equipo volvió a publicarse.',
  ERROR: 'No se llegó a cobrar: Mercado Pago no respondió al iniciar el pago.',
};

/// What Mercado Pago did afterwards to a payment PhoneTec already counts as approved. The
/// sale stands; somebody has to look at it in Mercado Pago.
export const providerStatusAlerts: Record<string, string> = {
  refunded: 'Mercado Pago registró una devolución de este pago. Revisalo en Mercado Pago.',
  charged_back: 'Mercado Pago registró un contracargo de este pago. Revisalo en Mercado Pago.',
  in_mediation: 'El comprador abrió una disputa por este pago en Mercado Pago. Revisala ahí.',
};

export function formatHistoryTimestamp(value: string) {
  return new Date(value).toLocaleString('es-UY', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function formatCalendarDate(value: string) {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Intl.DateTimeFormat('es-UY', { dateStyle: 'long', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, month - 1, day)),
  );
}

/// A counter sale keeps the calendar date the operator chose; an online one, the commercial day
/// of PhoneTec. The warranty of each sale reads the same way.
export function formatSaleDate(value: string, paymentMethod?: string | null) {
  if (paymentMethod === 'MERCADO_PAGO') {
    return new Intl.DateTimeFormat('es-UY', {
      dateStyle: 'long',
      timeZone: 'America/Montevideo',
    }).format(new Date(value));
  }
  return formatCalendarDate(value);
}

export function getNoteAuthorLabel(note: Pick<NoteEntry, 'kind' | 'author'>): string {
  if (note.kind === 'CUSTOMER') {
    return 'Cliente';
  }

  const name = note.author ? note.author.fullName : 'PhoneTec';
  return note.kind === 'SYSTEM' ? `Sistema · ${name}` : name;
}
